'use client';

import { createContext, useContext, useEffect, useState, startTransition, useCallback } from 'react';
import { useRouter } from 'next/router';
import { getContainer } from '@framework/di/getContainer';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { useHeaderMenuContext as useHeaderMenuContextV2 } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import { getFilters } from '@/creditflowresearch/application/TopCompanies/helpers';
import { IFilters } from '@/creditflowresearch/application/TopCompanies/interfaces';
import CompanyRepository from '@/creditflowresearch/infrastructure/repository/CompanyRepository';
import {
  selectProductAndRegionIdsFromQuery,
} from '@/creditflowresearch/application/PageFilters/selectProductAndRegionIdsFromQuery';
import { ICompanyByIdState } from '@/creditflowresearch/application/Deal/helpers';
import { ProfileTypes } from '@/creditflowresearch/application/actions/profile/constants';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { checkAccessByProducts } from '@/creditflowresearch/application/User/access/checkAccessByProducts';
import { checkAccessByRegions } from '@/creditflowresearch/application/User/access/checkAccessByRegions';
import {
  checkAccessByMarket as checkAccessByMarketHelper,
} from '@/creditflowresearch/application/User/access/checkAccessByMarket';
import { Product } from '@/creditflowresearch/domain/Product';
import { AbsProduct, HycProduct, IgcProduct } from '@/creditflowresearch/domain/Product';
import UserPlan from '@creditflowresearch/user-plan/UserPlan';
import { useMounted } from '@/creditflowresearch/ui/common/hooks';
import Market from '@/creditflowresearch/domain/Market';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';
import { PATH_ONLY_GRANT_CONTEXT } from '@/creditflowresearch/application/User/access/grants/pagePatterns';
import Region, { RegionType } from '@/creditflowresearch/domain/Region';

const usePageFilters = ({
  profileType,
  isConsumerOverview = false,
  company: initCompany,
  productIds: pageProductIds,
}) => {
  const container = getContainer();
  const [company, setCompany] = useState(initCompany || null);
  const [additionalFilters, setAdditionalFilters] = useState({});
  const router = useRouter();
  const { query, asPath } = router;
  const { industry, sector, companyViewId, leagueType } = query;
  const legacy = useContext(DictionariesContext);
  const v2HeaderMenu = useHeaderMenuContextV2();
  const regionsList = legacy?.regionsList ?? v2HeaderMenu.regions;
  const productsList = legacy?.productsList ?? v2HeaderMenu.products;
  const sectorsList = legacy?.sectorsList ?? v2HeaderMenu.mappedSectors;
  const industriesList = legacy?.industriesList ?? v2HeaderMenu.mappedIndustries;
  const mapping = legacy?.mapping ?? v2HeaderMenu.mapping;
  const {
    products: accessibleProductsIds,
    regions: accessibleRegionsIds,
    plan,
    preferences,
  } = useUserPlanContext();
  const granted = useAccessGrant();
  // TODO: remove when the European market opening period ends
  const isOpenAccessPage = useAccessGrant(PATH_ONLY_GRANT_CONTEXT);

  const { productIds: queryProductIds, regionIds } = selectProductAndRegionIdsFromQuery({
    pathname: asPath,
    query,
    regionsList,
    productsList,
    plan,
    accessibleProductsIds,
    accessibleRegionsIds,
    preferences,
  });

  const productIds = pageProductIds || queryProductIds || [];
  const marketIds = query.markets ? Market.getIds(query.markets as string) : [];

  const checkAccessByUserRegions = () => {
    return checkAccessByRegions(regionIds, accessibleRegionsIds);
  };

  const checkAccessByUserMarket = () => {
    return checkAccessByMarketHelper(marketIds, regionIds, accessibleRegionsIds);
  };

  const checkAccessByUserProducts = () => {
    return checkAccessByProducts(productIds, accessibleProductsIds);
  };

  const preparedFilters: IFilters = {
    profileType,
    isConsumerOverview,
    productIds,
    regionIds,
    industry: industry as string,
    sector: sector as string,
    sectorsList,
    industriesList,
    mapping,
    leagueType: leagueType as string,
    ...additionalFilters,
  };

  if (companyViewId && company) {
    preparedFilters.includedParentId = company.id;
  }

  const pageFilters = getFilters(preparedFilters);

  const { productIds: includedProductIds, regionIds: includedRegionIds } = pageFilters;

  // TODO: remove when the European market opening period ends
  const isProductOpenAccessible = (productId: string) =>
    granted && !Product.isOfType(productId, AbsProduct.TYPE);

  // TODO: remove when the European market opening period ends
  const productFilters = productsList.map((product) => ({
    ...product,
    isActive: includedProductIds.includes(product.id),
    tooltip: product.displayName,
    disabled: !isProductOpenAccessible(product.id)
              && (!accessibleProductsIds.includes(product.id) || !UserPlan.isSatisfiedPremiumPlan(plan)),
  }));

  const corporateProductFilters = productFilters.filter(({ id }) =>
    Product.isOfType(id, HycProduct.TYPE) || Product.isOfType(id, IgcProduct.TYPE),
  );

  /*
  * TODO: Remove this function when the European market opening period ends
  * */
  const getEuropeanAccess = (regionId: string) => granted && Region.isOfType(regionId, RegionType.EMEA);

  // TODO: remove when the European market opening period ends
  const regionFilters = regionsList.map((region) => ({
    ...region,
    isActive: includedRegionIds.includes(region.id),
    tooltip: `Includes deals issued with currencies or issuers domiciled in ${ region.name }`,
    disabled: !(isOpenAccessPage || getEuropeanAccess(region.id))
              && (!accessibleRegionsIds.includes(region.id) || !UserPlan.isSatisfiedPremiumPlan(plan)),
  }));

  const firstRegion = regionFilters.find(({ id }) => id === includedRegionIds[0]);
  const firstProduct = productFilters.find(({ id }) => id === includedProductIds[0]);

  const accessibleProducts = checkAccessByUserProducts() && checkAccessByUserRegions()
    ? productFilters.filter(({ id }) => accessibleProductsIds.includes(id))
    : [];
  const accessibleRegions = checkAccessByUserProducts() && checkAccessByUserRegions()
    ? regionFilters.filter(({ id }) => accessibleRegionsIds.includes(id))
    : [];

  const isMounted = useMounted();

  const getParentCompany = useCallback(async (): Promise<void> => {
    const companyRepository = container.get<CompanyRepository>(CompanyRepository);
    try {
      const company = await companyRepository.getCompanyByViewId({
        viewId: companyViewId as string,
      });
      startTransition(() => {
        setCompany(company);
      });
    } catch (e) {
      // @todo throw redirect server side
      await router.push('/404');
    }
  }, [container, companyViewId, router]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }
    if (initCompany?.hasServerError) {
      getParentCompany();
    }
  }, [initCompany, isMounted, getParentCompany]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }
    startTransition(() => {
      setCompany(initCompany);
    });
  }, [initCompany, isMounted]);

  return {
    company,
    productFilters,
    regionFilters,
    corporateProductFilters,
    includedProductIds,
    includedRegionIds,
    firstRegion,
    firstProduct,
    pageFilters,
    accessibleProducts,
    accessibleRegions,
    additionalFilters,
    setAdditionalFilters,
    checkAccessByUserRegions,
    checkAccessByUserMarket,
    checkAccessByUserProducts,
  };
};

type PageFiltersContextType = ReturnType<typeof usePageFilters>;

export const PageFiltersContext = createContext<PageFiltersContextType>(null);

export const usePageFiltersContext = () => {
  const context = useContext(PageFiltersContext);
  if (!context) {
    throw new Error('usePageFilters must be used within the PageFiltersContext');
  }
  return context;
};

interface IProps {
  profileType?: ProfileTypes,
  isConsumerOverview?: boolean,
  company?: ICompanyByIdState,
  productIds?: string[],
  children: React.ReactNode,
}

const PageFiltersContextProvider = ({ children, profileType, isConsumerOverview, company, productIds }: IProps) => (
  <PageFiltersContext.Provider value={ usePageFilters({ profileType, isConsumerOverview, company, productIds }) }>
    { children }
  </PageFiltersContext.Provider>
);

export default PageFiltersContextProvider;
